const MAX_DEEP_LINK_LENGTH = 8192;
const ISSUE_NAME_PATTERN = /^issues\/[A-Za-z0-9._~-]+$/;

export type CodingSessionApplication = 'Xirp' | 'Xirp for Spotify';

const DEEP_LINK_BASES: Record<CodingSessionApplication, string> = {
  Xirp: 'web+xirp://launch',
  'Xirp for Spotify': 'xirp-internal://launch',
};

export const CODING_SESSION_APPLICATION_FALLBACKS: Record<
  CodingSessionApplication,
  { label: string; url: string }
> = {
  Xirp: { label: 'Get Xirp', url: 'https://xirp.spotify.com/' },
  'Xirp for Spotify': {
    label: 'Get Xirp for Spotify',
    url: 'https://backstage.spotify.net/docs/default/component/chirp/#install',
  },
};

export const CODING_SESSION_APPLICATION_TRACK_IDS: Record<
  CodingSessionApplication,
  string
> = {
  Xirp: 'xirp',
  'Xirp for Spotify': 'xirp-for-spotify',
};

export type RepositoryIdentity = {
  host?: string | null;
  htmlUrl?: string | null;
  name: string;
};

const getRepositoryUrl = (repository: RepositoryIdentity): string => {
  const resourceNameSegments = repository.name.split('/');
  const repositoryUrl =
    repository.htmlUrl ??
    (repository.host && resourceNameSegments.length === 4
      ? `https://${repository.host}/${resourceNameSegments[2]}/${resourceNameSegments[3]}`
      : null);
  if (!repositoryUrl) {
    throw new Error(
      'The selected repository does not include enough information to launch a coding session.',
    );
  }

  let url: URL;
  try {
    url = new URL(repositoryUrl);
  } catch {
    throw new Error('The selected repository has an invalid URL.');
  }

  const pathSegments = url.pathname.split('/');
  const repositoryName = pathSegments[2]?.replace(/\.git$/i, '') ?? '';
  if (
    url.protocol !== 'https:' ||
    url.username ||
    url.password ||
    url.port ||
    url.search ||
    url.hash ||
    repositoryUrl.includes('\\') ||
    /%2f|%5c/i.test(url.pathname) ||
    pathSegments.length !== 3 ||
    pathSegments[0] !== '' ||
    !pathSegments[1] ||
    !repositoryName
  ) {
    throw new Error(
      'The selected repository URL must be a plain HTTPS owner/repository URL.',
    );
  }

  return `${url.origin}/${pathSegments[1]}/${repositoryName}`;
};

export const buildCodingSessionLaunchUrl = ({
  application,
  baseBranch,
  goal,
  repository,
}: {
  application: CodingSessionApplication;
  baseBranch?: string;
  goal: string;
  repository: RepositoryIdentity;
}): string => {
  if (!goal.trim()) {
    throw new Error('The coding session goal cannot be empty.');
  }

  const url = new URL(DEEP_LINK_BASES[application]);
  url.search = new URLSearchParams({
    action: 'create-session',
    repositoryUrl: getRepositoryUrl(repository),
    goal,
    ...(baseBranch ? { baseBranch } : {}),
  }).toString();

  const launchUrl = url.toString();
  if (launchUrl.length > MAX_DEEP_LINK_LENGTH) {
    throw new Error(`The ${application} launch URL is too long.`);
  }

  return launchUrl;
};

export const buildIssueCodingSessionLaunchUrl = ({
  application,
  issueName,
  repository,
}: {
  application: CodingSessionApplication;
  issueName: string;
  repository: RepositoryIdentity;
}): string => {
  if (!ISSUE_NAME_PATTERN.test(issueName)) {
    throw new Error('The issue has an invalid resource name.');
  }

  return buildCodingSessionLaunchUrl({
    application,
    goal: `Implement Confidence issue ${issueName}. Use the Confidence Issues MCP server to fetch context for ${issueName} before planning or editing.`,
    repository,
  });
};

export const buildCodeReviewXirpForSpotifyLaunchUrl = ({
  pullRequestBranch,
  pullRequestNumber,
  pullRequestUrl,
  repositoryName,
}: {
  pullRequestBranch: string;
  pullRequestNumber: number;
  pullRequestUrl: string;
  repositoryName: string;
}): string => {
  const normalizedPullRequestBranch = pullRequestBranch.trim();
  if (!normalizedPullRequestBranch) {
    throw new Error('The pull request branch cannot be empty.');
  }

  let url: URL;
  try {
    url = new URL(pullRequestUrl);
  } catch {
    throw new Error('The pull request has an invalid URL.');
  }

  const pathSegments = url.pathname.split('/');
  const repositoryNameSegments = repositoryName.split('/');
  if (
    url.protocol !== 'https:' ||
    url.username ||
    url.password ||
    url.port ||
    url.search ||
    url.hash ||
    pullRequestUrl.includes('\\') ||
    /%2f|%5c/i.test(url.pathname) ||
    pathSegments.length !== 5 ||
    pathSegments[0] !== '' ||
    !pathSegments[1] ||
    !pathSegments[2] ||
    pathSegments[3] !== 'pull' ||
    !Number.isSafeInteger(pullRequestNumber) ||
    pullRequestNumber < 1 ||
    pathSegments[4] !== String(pullRequestNumber) ||
    repositoryNameSegments.length !== 4 ||
    repositoryNameSegments[2] !== pathSegments[1] ||
    repositoryNameSegments[3] !== pathSegments[2]
  ) {
    throw new Error(
      'The pull request URL must match its HTTPS repository and number.',
    );
  }

  const canonicalPullRequestUrl = `${url.origin}/${pathSegments[1]}/${pathSegments[2]}/pull/${pullRequestNumber}`;

  return buildCodingSessionLaunchUrl({
    application: 'Xirp for Spotify',
    baseBranch: normalizedPullRequestBranch,
    goal: `Review pull request ${canonicalPullRequestUrl}. Inspect the changes, run relevant checks, and report findings before making edits.`,
    repository: {
      htmlUrl: `${url.origin}/${pathSegments[1]}/${pathSegments[2]}`,
      name: repositoryName,
    },
  });
};
